import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User, Camera, ShieldCheck } from "lucide-react";
import { useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface ProfileHeaderProps {
  profile?: {
    id: string;
    name: string;
    email: string;
    phone_number: string;
    avatar_url: string;
    created_at: string;
  };
  roles?: {
    is_admin: boolean;
    is_platform_admin: boolean;
    is_org_admin: boolean;
    platform_role: string;
    org_role: string;
  };
  isLoading?: boolean;
}

export const ProfileHeader = ({ profile, roles, isLoading }: ProfileHeaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadge = () => {
    if (roles?.is_platform_admin) {
      return (
        <Badge variant="secondary" className="bg-primary/10 text-primary">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Super Admin
        </Badge>
      );
    }
    if (roles?.is_org_admin) {
      return (
        <Badge variant="secondary">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Org Admin
        </Badge>
      );
    }
    if (roles?.is_admin) {
      return (
        <Badge variant="secondary">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Admin
        </Badge>
      );
    }
    return null;
  };

  const formatMemberSince = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `Member since ${date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    })}`;
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile?.id) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.id}/${crypto.randomUUID()}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile-complete'] });
      
      toast({
        title: "Avatar updated",
        description: "Your profile picture has been updated successfully.",
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Upload failed",
        description: "Failed to update profile picture. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center space-x-6">
            <div className="h-24 w-24 rounded-full bg-muted animate-pulse" />
            <div className="space-y-3">
              <div className="h-6 w-48 bg-muted animate-pulse rounded" />
              <div className="h-4 w-64 bg-muted animate-pulse rounded" />
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-8">
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
          {/* Avatar Section */}
          <div className="relative group">
            <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
              <AvatarImage src={profile?.avatar_url} className="object-cover" />
              <AvatarFallback className="text-2xl font-semibold bg-primary/10 text-primary">
                {getInitials(profile?.name)}
              </AvatarFallback>
            </Avatar>
            
            {/* Upload Overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full flex items-center justify-center cursor-pointer"
                 onClick={() => fileInputRef.current?.click()}>
              <Camera className="h-6 w-6 text-white" />
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>

          {/* Profile Info */}
          <div className="flex-1 space-y-2">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <h1 className="text-2xl font-bold">
                {profile?.name || 'Complete your profile'}
              </h1>
              {getRoleBadge()}
            </div>
            
            <div className="space-y-1 text-muted-foreground">
              {profile?.email && (
                <p className="flex items-center gap-2">
                  <span>📧</span>
                  {profile.email}
                </p>
              )}
              {profile?.phone_number && (
                <p className="flex items-center gap-2">
                  <span>📱</span>
                  {profile.phone_number}
                </p>
              )}
              <p className="text-sm">
                {formatMemberSince(profile?.created_at)}
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex-shrink-0">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Camera className="h-4 w-4 mr-2" />
              Change Photo
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};